import {useAtom} from 'jotai';
import {isEqual} from 'lodash';
import {useCallback, useEffect, useMemo} from 'react';
import {LoadingBar} from '@/components/LoadingBar';
import {AnalysisItem, DaycareCenterData, KindergartenData} from '@/map/@types';
import {useNearbyChildcareCenterByPoint} from '@/map/api';
import {selectedNearbyInfoItemCardAtom} from '@/map/atom';
import {AROUND_DATA_OPTIONS} from '@/map/constants';
import {AreaAnalyticsFilter} from '@/map/enums';
import {ColumnFlexBox} from '@/styled-components';
import {palette} from '@/styles';
import {withCommaWithUnit} from '@/utils';
import {SpotRoundMark} from '../../../Marker';
import {
  DaycareCenterMarkers,
  KindergartenMarkers,
} from '../../../MarkerCluster/NearbyInfo';
import {ErrorBox} from '../ErrorBox';
import {NoData} from '../NoData';
import {ReportContainer} from '../StyledComponents';
import {NearbyInfoTable} from './Table';

interface IProps {
  map?: naver.maps.Map;
  item: AnalysisItem;
}

export const ChildcareCentersReport = ({map, item}: IProps) => {
  const [activeItem, setActiveItem] = useAtom(selectedNearbyInfoItemCardAtom);

  const {data, isPending, error} = useNearbyChildcareCenterByPoint(item);

  const tableData = useMemo(() => {
    if (data) {
      return Object.entries(data).reduce((acc, [key, value]) => {
        if (Array.isArray(value)) {
          acc[key] = value.map((i) => [
            i.name,
            withCommaWithUnit(
              (i as DaycareCenterData).childrenCount ||
                (i as KindergartenData).studentsCount,
              {placeholder: '정보 없음', unit: '명'}
            ),
          ]);
        }
        return acc;
      }, {} as any);
    }
  }, [data]);

  const onMarkerClick = useCallback(
    ({
      type,
      index,
    }: {
      type:
        | AreaAnalyticsFilter.DAYCARE_CENTER
        | AreaAnalyticsFilter.KINDERGARTEN;
      index: number;
    }) => {
      if (!data) {
        return;
      }

      const item = data[type]?.[index];

      setActiveItem((prev) => {
        if (prev?.type === type && isEqual(item, prev.item)) {
          return null;
        }

        return {
          type,
          item,
        };
      });
    },
    [data, setActiveItem]
  );

  useEffect(() => {
    return () => {
      setActiveItem(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    return (
      <ReportContainer>
        <ErrorBox />
      </ReportContainer>
    );
  } else if (isPending) {
    return (
      <ReportContainer>
        <LoadingBar color={palette.textSecondary} />
      </ReportContainer>
    );
  }

  const noData =
    tableData &&
    !tableData[AreaAnalyticsFilter.DAYCARE_CENTER]?.length &&
    !tableData[AreaAnalyticsFilter.KINDERGARTEN]?.length;

  if (noData) {
    return (
      <ReportContainer>
        <NoData text="분석 지역 내 보육시설 정보가 없습니다." />
      </ReportContainer>
    );
  }

  return (
    <>
      <DaycareCenterMarkers
        map={map}
        items={data?.[AreaAnalyticsFilter.DAYCARE_CENTER]}
        selectedItem={
          activeItem?.type === AreaAnalyticsFilter.DAYCARE_CENTER
            ? activeItem.item
            : undefined
        }
        onClick={onMarkerClick}
      />
      <KindergartenMarkers
        map={map}
        items={data?.[AreaAnalyticsFilter.KINDERGARTEN]}
        selectedItem={
          activeItem?.type === AreaAnalyticsFilter.KINDERGARTEN
            ? activeItem.item
            : undefined
        }
        onClick={onMarkerClick}
      />
      <ReportContainer>
        <ColumnFlexBox gap={40}>
          <NearbyInfoTable
            title="어린이집"
            headers={[
              {width: '60%', label: '어린이집'},
              {width: '40%', label: '원아 수'},
            ]}
            sumLabel="어린이집이"
            icon={<Icon type={AreaAnalyticsFilter.DAYCARE_CENTER} />}
            data={tableData[AreaAnalyticsFilter.DAYCARE_CENTER]}
          />
          <NearbyInfoTable
            title="유치원"
            headers={[
              {width: '60%', label: '유치원'},
              {width: '40%', label: '원아 수'},
            ]}
            sumLabel="유치원이"
            icon={<Icon type={AreaAnalyticsFilter.KINDERGARTEN} />}
            data={tableData[AreaAnalyticsFilter.KINDERGARTEN]}
          />
        </ColumnFlexBox>
      </ReportContainer>
    </>
  );
};

const Icon = ({type}: {type: AreaAnalyticsFilter}) => {
  const options = AROUND_DATA_OPTIONS[type];

  return <SpotRoundMark typeLabel={options.label} {...options.mobile} />;
};
